import type { ReactNode } from 'react';

import { useAuth } from '@/auth/useAuth';
import { ErrorScreen } from '@/components/ErrorScreen';
import { LoadingScreen } from '@/components/LoadingScreen';
import { NotFound } from '@/routes/NotFound';

import { SupportReauthPrompt } from './SupportReauthPrompt';
import { useSupportConsoleAccess } from './useSupportConsole';

// Decides what someone who opens the support console's address sees.
//
// The console is shown ONLY once the backend has answered "granted" for the
// person signed in. Every other answer ends at the "Page not found" an unknown
// address shows: a refusal, an answer that cannot be read, an answer nobody
// expected. Two more things can appear, and neither carries any data:
//
//   - "sign in again", which the backend sends only to support staff whose
//     sign-in is too old;
//   - a retry, when the backend could not be reached at all.
//
// This is not what keeps people out. The backend refuses every support call
// from anyone it would refuse here, and the browser's idea of who is signed in
// counts for nothing there. The gate only keeps the page from being drawn.
export function SupportConsoleGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const access = useSupportConsoleAccess();

  if (!user) {
    return <NotFound />;
  }

  if (access.isPending) {
    return <LoadingScreen message="Loading…" />;
  }

  // Before the data: a check that failed leaves the last answer in place, and
  // an old "granted" must never outlive a newer refusal.
  if (access.isError) {
    if (access.error?.kind === 'network') {
      return (
        <ErrorScreen
          message="Could not reach Kudu. Check your connection and try again."
          onRetry={() => void access.refetch()}
        />
      );
    }

    return <NotFound />;
  }

  if (access.data?.reauthRequired === true) {
    return <SupportReauthPrompt maxSessionHours={access.data.maxSessionHours} />;
  }

  if (access.data?.granted === true) {
    return <>{children}</>;
  }

  return <NotFound />;
}
